import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material';
import { TreeViewItem } from '@core/components/fc-tree-view/fc-tree.types';
import { VideoInfoModalService } from '@core/components/video-info-modal/video-info-modal.service';
import { Equipment } from '@core/interfaces';
import { LocationEnvironment } from '@core/interfaces/locations-environments';
import { TranslateService } from '@ngx-translate/core';
import { merge, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { DialogLinkComponent } from './dialog-link/dialog-link.component';
import { DialogLinkData, GetLinkedItemsResult } from './dialog-link/dialog-link.types';
import { TabLinksService } from './tab-links.service';
import { ReloadItemSubject, UnlinkItemSubject } from './tab-links.types';

@Component({
  selector: 'tab-links',
  templateUrl: './tab-links.component.html',
  styleUrls: ['./tab-links.component.scss']
})
export class TabLinksComponent implements OnInit, OnDestroy {
  @Input()
  public maintenanceForm: FormGroup;

  public searchControl = new FormControl();
  public hasLinkedItems = false;

  public locationLinks: TreeViewItem[] = [];
  public unlinkedEquipments: TreeViewItem[] = [];
  public destroy$ = new Subject();

  constructor(
    private readonly matDialog: MatDialog,
    private readonly tabLinksService: TabLinksService,
    private readonly translateService: TranslateService,
    private readonly videoInfoModalService: VideoInfoModalService,
  ) { }

  public ngOnInit(): void {
    this.tabLinksService.unlinkItem$
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: UnlinkItemSubject) => {
        this.clearFormLinks(result);
        this.populateLinks();
      });

    this.tabLinksService.reloadItem$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: ReloadItemSubject) => {
        this.updateFormLinks(value);
        this.populateLinks();
      });

    this.searchControl.valueChanges
      .pipe(takeUntil(this.destroy$), debounceTime(300))
      .subscribe(() => {
        this.populateLinks();
      });

    merge(this.maintenanceForm.get('segment').valueChanges, this.maintenanceForm.get('location').valueChanges)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.populateLinks();
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public openDialogLink(): void {
    const segment = this.maintenanceForm.get('segment').value;
    const location = this.maintenanceForm.get('location').value;

    if (!segment || !location) {
      return;
    }

    this.matDialog.open(DialogLinkComponent, {
      panelClass: 'field-dialog-860',
      autoFocus: false,
      data: {
        locationId: location.id,
        segmentId: segment.id,
        linkedEquipments: this.maintenanceForm.get('equipments').value,
        linkedSectors: this.maintenanceForm.get('locationSectors').value,
        linkedEnvironments: this.maintenanceForm.get('locationEnvironments').value,
      } as DialogLinkData
    }).afterClosed().subscribe((result?: GetLinkedItemsResult) => {
      if (result) {
        this.maintenanceForm.get('equipments').setValue(result.equipments);
        this.maintenanceForm.get('locationSectors').setValue(result.sectors);
        this.maintenanceForm.get('locationEnvironments').setValue(result.environments);
      }

      this.searchControl.setValue('');
      this.populateLinks();
    });
  }

  private updateFormLinks(params: ReloadItemSubject): void {
    const formValue = this.maintenanceForm.get(params.formControlName);
    const index = formValue.value.findIndex((item) => item.id === params.updatedValue.id);
    formValue.value[index] = params.updatedValue;
    this.maintenanceForm.get(params.formControlName).setValue(formValue.value);

    if (params.updatedValue.archived) {
      formValue.value.splice(index, 1);
      this.clearFormLinks({
        id: params.updatedValue.id,
        formControlName: params.formControlName
      });
    }

    if (params.formControlName = 'equipments') {
      const hasEnvironmentOnForm = params.updatedValue.locationEnvironment && this.maintenanceForm.get('locationEnvironments').value
        .some((environment) => environment.id === params.updatedValue.locationEnvironment.id);

      if (params.updatedValue.locationEnvironment && !hasEnvironmentOnForm) {
        formValue.value.splice(index, 1);
        this.clearFormLinks({
          id: params.updatedValue.id,
          formControlName: params.formControlName
        });
      }

      const hasSectorOnForm = params.updatedValue.locationSector && this.maintenanceForm.get('locationSectors').value
        .some((sector) => sector.id === params.updatedValue.locationSector.id);

      if (params.updatedValue.locationSector && !hasSectorOnForm) {
        formValue.value.splice(index, 1);
        this.clearFormLinks({
          id: params.updatedValue.id,
          formControlName: params.formControlName
        });
      }
    }

    this.populateLinks();
  }

  private clearFormLinks(params: UnlinkItemSubject): void {
    const maintenanceFormValue = this.maintenanceForm.getRawValue();
    const newValue = maintenanceFormValue[params.formControlName].filter((item) => item.id !== params.id);
    this.maintenanceForm.get(params.formControlName).setValue(newValue);

    if (params.formControlName === 'locationSectors') {
      const environments = maintenanceFormValue.locationEnvironments.filter((environment: LocationEnvironment) => (
        environment.locationSector.id !== params.id
      ));

      const equipments = maintenanceFormValue.equipments.filter((equipment: Equipment) => (
        !equipment.locationSector || equipment.locationSector.id !== params.id
      ));

      this.maintenanceForm.get('equipments').setValue(equipments);
      this.maintenanceForm.get('locationEnvironments').setValue(environments);
    }

    if (params.formControlName === 'locationEnvironments') {
      const equipments = maintenanceFormValue.equipments.filter((equipment: Equipment) => (
        !equipment.locationEnvironment || equipment.locationEnvironment.id !== params.id
      ));

      this.maintenanceForm.get('equipments').setValue(equipments);
    }
  }

  private populateLinks(): void {
    this.hasLinkedItems = Boolean(
      this.maintenanceForm.get('equipments').value.length ||
      this.maintenanceForm.get('locationSectors').value.length ||
      this.maintenanceForm.get('locationEnvironments').value.length
    );

    const equipments = this.maintenanceForm.get('equipments').value;
    const sectors = this.maintenanceForm.get('locationSectors').value;
    const environments = this.maintenanceForm.get('locationEnvironments').value;

    const searchValue = this.searchControl.value && this.searchControl.value.trim().toLowerCase();

    const { sectors: sectorsLinks, unlinkedEquipments } = this.tabLinksService.linkSectorsEnvironmentsEquipments({
      sectors,
      environments,
      equipments
    });

    const locationLinksUnfiltered = this.tabLinksService.formatSectors({
      sectors: sectorsLinks,
      environments,
      equipments
    });

    const unlinkedEquipmentsUnfiltered = this.tabLinksService.formatUnlinkedEquipments({
      unlinkedEquipments
    });

    this.locationLinks = this.tabLinksService.filter(locationLinksUnfiltered, searchValue);
    this.unlinkedEquipments = this.tabLinksService.filter(unlinkedEquipmentsUnfiltered, searchValue);
  }

  public openDemonstrationVideoDialog(): void {
    this.videoInfoModalService.open({
      title: this.translateService.instant('MAINTENANCES.CREATE_DIALOG.TAB_LINKS.FEATURE.INTRO_VIDEO.TITLE'),
      description: this.translateService.instant('MAINTENANCES.CREATE_DIALOG.TAB_LINKS.FEATURE.INTRO_VIDEO.DESCRIPTION'),
      videoId: 'VQE_FyV8Gf0'
    });
  }
}
